import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";

import { db, storage } from "../firebase";
import { guestIdentityKey, provisionGuestProfile } from "../utils/guestIdentity";

/* ==========================================================================
   CONSTANTS
========================================================================== */

const GUEST_PROFILE_COLLECTION = "guestProfiles";

const MAX_IMAGE_SIZE_BYTES =
  5 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

/* ==========================================================================
   BASIC HELPERS
========================================================================== */

export const cleanGuestValue = (
  value
) => {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
};

export const lowerGuestValue = (
  value
) => {
  return cleanGuestValue(
    value
  ).toLowerCase();
};

export const normalizeGuestPhone = (
  value
) => {
  return String(value ?? "").replace(
    /\D/g,
    ""
  );
};

export const normalizeGuestIdNumber = (
  value
) => {
  return cleanGuestValue(value)
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();
};

const toSafeNumber = (
  value,
  fallback = 0
) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

const normalizeFileName = (
  value
) => {
  return cleanGuestValue(value)
    .replace(
      /[^a-zA-Z0-9._-]/g,
      "_"
    )
    .replace(/_+/g, "_");
};

const isFileObject = (value) => {
  if (!value) return false;

  if (
    typeof File !== "undefined" &&
    value instanceof File
  ) {
    return true;
  }

  if (
    typeof Blob !== "undefined" &&
    value instanceof Blob
  ) {
    return true;
  }

  return false;
};

const getTimestampValue = (
  value
) => {
  if (!value) return 0;

  if (
    typeof value.toMillis ===
    "function"
  ) {
    return value.toMillis();
  }

  if (
    typeof value.toDate ===
    "function"
  ) {
    return value
      .toDate()
      .getTime();
  }

  const date = new Date(value);
  const time = date.getTime();

  return Number.isNaN(time)
    ? 0
    : time;
};

const getProfileSortTime = (
  profile
) => {
  return Math.max(
    getTimestampValue(
      profile?.updatedAt
    ),
    getTimestampValue(
      profile?.lastVisitAt
    ),
    getTimestampValue(
      profile?.lastVisitedAt
    ),
    getTimestampValue(
      profile?.createdAt
    )
  );
};

const getFileExtension = (
  file
) => {
  const originalName =
    normalizeFileName(
      file?.name || ""
    );

  if (
    originalName.includes(".")
  ) {
    const extension =
      originalName
        .split(".")
        .pop();

    if (extension) {
      return extension.toLowerCase();
    }
  }

  const mimeType =
    lowerGuestValue(
      file?.type
    );

  if (
    mimeType === "image/png"
  ) {
    return "png";
  }

  if (
    mimeType === "image/webp"
  ) {
    return "webp";
  }

  return "jpg";
};

/* ==========================================================================
   PROFILE NORMALIZATION
========================================================================== */

export const normalizeGuestProfile = (
  profile = {}
) => {
  const profileId =
    cleanGuestValue(
      profile.profileId ??
        profile.id ??
        profile.guestProfileId
    );

  const phoneNumber =
    normalizeGuestPhone(
      profile.phoneNumber ??
        profile.phone
    );

  const idNumber =
    normalizeGuestIdNumber(
      profile.idNumber
    );

  const guestTag =
    lowerGuestValue(
      profile.guestTag
    );

  return {
    ...profile,

    id: profileId,
    profileId,

    hotelId:
      cleanGuestValue(
        profile.hotelId
      ),

    hotelName:
      cleanGuestValue(
        profile.hotelName
      ),

    name:
      cleanGuestValue(
        profile.name
      ),

    nameLower:
      lowerGuestValue(
        profile.name
      ),

    phoneNumber,
    phone: phoneNumber,

    age:
      profile.age === "" ||
      profile.age === null ||
      profile.age === undefined
        ? ""
        : toSafeNumber(
            profile.age
          ),

    gender:
      cleanGuestValue(
        profile.gender
      ),

    nationality:
      cleanGuestValue(
        profile.nationality
      ) || "Indian",

    address:
      cleanGuestValue(
        profile.address
      ),

    relation:
      cleanGuestValue(
        profile.relation
      ),

    idType:
      cleanGuestValue(
        profile.idType
      ) || "Aadhaar Card",

    idNumber,

    idNumberLower:
      lowerGuestValue(
        idNumber
      ),

    idFrontUrl:
      cleanGuestValue(
        profile.idFrontUrl
      ),

    idBackUrl:
      cleanGuestValue(
        profile.idBackUrl
      ),

    visitCount:
      toSafeNumber(
        profile.visitCount
      ),

    isVip:
      profile.isVip === true ||
      guestTag === "vip",

    isBlacklisted:
      profile.isBlacklisted ===
        true ||
      guestTag === "blacklist",

    blacklistReason:
      cleanGuestValue(
        profile.blacklistReason
      ),

    notes:
      cleanGuestValue(
        profile.profileNotes ??
          profile.notes
      ),

    searchText:
      cleanGuestValue(
        profile.searchText
      ),
  };
};

/* ==========================================================================
   SEARCH TEXT
========================================================================== */

export const buildGuestSearchText = (
  guest = {}
) => {
  const profile =
    normalizeGuestProfile(
      guest
    );

  const values = [
    profile.name,
    profile.nameLower,
    profile.phoneNumber,
    profile.idType,
    lowerGuestValue(
      profile.idType
    ),
    profile.idNumber,
    profile.idNumberLower,
    profile.address,
    lowerGuestValue(
      profile.address
    ),
    profile.nationality,
    lowerGuestValue(
      profile.nationality
    ),
    profile.gender,
    lowerGuestValue(
      profile.gender
    ),
    profile.relation,
    lowerGuestValue(
      profile.relation
    ),
    profile.hotelName,
    lowerGuestValue(
      profile.hotelName
    ),
  ];

  return [
    ...new Set(
      values.filter(Boolean)
    ),
  ].join(" ");
};

/* ==========================================================================
   LOAD GUEST PROFILES
========================================================================== */

/**
 * Dono forms support karta hai:
 *
 * loadGuestProfiles("hotelId")
 *
 * loadGuestProfiles({
 *   hotelId: "hotelId",
 *   includeBlacklisted: true
 * })
 */

export async function loadGuestProfiles(
  hotelIdOrOptions
) {
  const options =
    typeof hotelIdOrOptions ===
      "object" &&
    hotelIdOrOptions !== null
      ? hotelIdOrOptions
      : {
          hotelId:
            hotelIdOrOptions,
        };

  const {
    hotelId,
    includeBlacklisted = true,
  } = options;

  const normalizedHotelId =
    cleanGuestValue(
      hotelId
    );

  if (!normalizedHotelId) {
    return [];
  }

  const profilesQuery =
    query(
      collection(
        db,
        GUEST_PROFILE_COLLECTION
      ),
      where(
        "hotelId",
        "==",
        normalizedHotelId
      )
    );

  const snapshot =
    await getDocs(
      profilesQuery
    );

  const profiles =
    snapshot.docs.map(
      (profileDocument) => {
        return normalizeGuestProfile(
          {
            id:
              profileDocument.id,

            profileId:
              profileDocument.id,

            ...profileDocument.data(),
          }
        );
      }
    );

  return profiles
    .filter((profile) => {
      if (
        includeBlacklisted
      ) {
        return true;
      }

      return !profile.isBlacklisted;
    })
    .sort(
      (
        firstProfile,
        secondProfile
      ) => {
        const timeDifference =
          getProfileSortTime(
            secondProfile
          ) -
          getProfileSortTime(
            firstProfile
          );

        if (
          timeDifference !== 0
        ) {
          return timeDifference;
        }

        return String(
          firstProfile.name || ""
        ).localeCompare(
          String(
            secondProfile.name ||
              ""
          )
        );
      }
    );
}

/* ==========================================================================
   FILTER GUEST PROFILES
========================================================================== */

export function filterGuestProfiles(
  profiles = [],
  searchValue = "",
  options = {}
) {
  const {
    limit = 10,
    includeBlacklisted = true,
    hotelId = "",
  } = options;

  const rawSearch =
    cleanGuestValue(
      searchValue
    );

  const normalizedSearch =
    lowerGuestValue(
      rawSearch
    );

  const phoneSearch =
    normalizeGuestPhone(
      rawSearch
    );

  const idSearch =
    normalizeGuestIdNumber(
      rawSearch
    );

  if (
    !normalizedSearch &&
    !phoneSearch &&
    !idSearch
  ) {
    return [];
  }

  return (profiles || [])
    .map(
      normalizeGuestProfile
    )
    .filter((profile) => {
      if (
        hotelId &&
        profile.hotelId !==
          cleanGuestValue(
            hotelId
          )
      ) {
        return false;
      }

      if (
        !includeBlacklisted &&
        profile.isBlacklisted
      ) {
        return false;
      }

      return true;
    })
    .map((profile) => {
      let score = 0;

      const profileName =
        lowerGuestValue(
          profile.name
        );

      const searchText =
        lowerGuestValue(
          profile.searchText ||
            buildGuestSearchText(
              profile
            )
        );

      if (
        phoneSearch &&
        profile.phoneNumber ===
          phoneSearch
      ) {
        score += 1000;
      } else if (
        phoneSearch &&
        profile.phoneNumber.startsWith(
          phoneSearch
        )
      ) {
        score += 700;
      } else if (
        phoneSearch &&
        profile.phoneNumber.includes(
          phoneSearch
        )
      ) {
        score += 450;
      }

      if (
        idSearch &&
        profile.idNumber ===
          idSearch
      ) {
        score += 950;
      } else if (
        idSearch &&
        profile.idNumber.startsWith(
          idSearch
        )
      ) {
        score += 650;
      } else if (
        idSearch &&
        profile.idNumber.includes(
          idSearch
        )
      ) {
        score += 400;
      }

      if (
        normalizedSearch &&
        profileName ===
          normalizedSearch
      ) {
        score += 900;
      } else if (
        normalizedSearch &&
        profileName.startsWith(
          normalizedSearch
        )
      ) {
        score += 600;
      } else if (
        normalizedSearch &&
        profileName.includes(
          normalizedSearch
        )
      ) {
        score += 350;
      }

      if (
        normalizedSearch &&
        lowerGuestValue(
          profile.address
        ).includes(
          normalizedSearch
        )
      ) {
        score += 120;
      }

      if (
        normalizedSearch &&
        searchText.includes(
          normalizedSearch
        )
      ) {
        score += 100;
      }

      if (profile.isVip && score > 0) {
        score += 10;
      }

      return {
        profile,
        score,
      };
    })
    .filter(
      (item) =>
        item.score > 0
    )
    .sort(
      (
        firstItem,
        secondItem
      ) => {
        if (
          firstItem.score !==
          secondItem.score
        ) {
          return (
            secondItem.score -
            firstItem.score
          );
        }

        const visitDifference =
          toSafeNumber(
            secondItem.profile
              .visitCount
          ) -
          toSafeNumber(
            firstItem.profile
              .visitCount
          );

        if (
          visitDifference !== 0
        ) {
          return visitDifference;
        }

        return String(
          firstItem.profile.name ||
            ""
        ).localeCompare(
          String(
            secondItem.profile
              .name || ""
          )
        );
      }
    )
    .slice(
      0,
      Math.max(
        1,
        toSafeNumber(
          limit,
          10
        )
      )
    )
    .map(
      (item) =>
        item.profile
    );
}

/* ==========================================================================
   IMAGE VALIDATION
========================================================================== */

export function validateGuestImageFile(
  file,
  options = {}
) {
  const {
    maxSizeBytes =
      MAX_IMAGE_SIZE_BYTES,

    allowedTypes =
      ALLOWED_IMAGE_TYPES,
  } = options;

  if (!file) {
    return {
      valid: true,
      error: "",
      message: "",
    };
  }

  if (!isFileObject(file)) {
    const message =
      "Selected ID document valid image file nahi hai.";

    return {
      valid: false,
      error: message,
      message,
    };
  }

  const fileType =
    lowerGuestValue(
      file.type
    );

  if (
    fileType &&
    !allowedTypes.includes(
      fileType
    )
  ) {
    const message =
      "Sirf JPG, JPEG, PNG ya WEBP image upload karo.";

    return {
      valid: false,
      error: message,
      message,
    };
  }

  if (
    Number(file.size || 0) >
    maxSizeBytes
  ) {
    const maxSizeMb =
      Math.round(
        maxSizeBytes /
          (1024 * 1024)
      );

    const message =
      `ID photo maximum ${maxSizeMb} MB honi chahiye.`;

    return {
      valid: false,
      error: message,
      message,
    };
  }

  return {
    valid: true,
    error: "",
    message: "",
  };
}

/* ==========================================================================
   GUEST PROFILE VALIDATION
========================================================================== */

export function validateGuestProfile(
  guest = {},
  options = {}
) {
  const {
    requirePhone = true,
    requireId = true,
    requireAddress = true,
    requireAge = false,
    requireIdFront = true,
  } = options;

  const normalized =
    normalizeGuestProfile(
      guest
    );

  const errors = {};

  if (!normalized.name) {
    errors.name =
      "Guest name required hai.";
  }

  if (
    requirePhone &&
    !normalized.phoneNumber
  ) {
    errors.phoneNumber =
      "Phone number required hai.";
  } else if (
    normalized.phoneNumber &&
    !/^\d{10,15}$/.test(
      normalized.phoneNumber
    )
  ) {
    errors.phoneNumber =
      "Valid phone number enter karo.";
  }

  if (
    requireAge &&
    normalized.age === ""
  ) {
    errors.age =
      "Age required hai.";
  } else if (
    normalized.age !== "" &&
    (
      Number(
        normalized.age
      ) < 1 ||
      Number(
        normalized.age
      ) > 120
    )
  ) {
    errors.age =
      "Valid age enter karo.";
  }

  if (
    !normalized.nationality
  ) {
    errors.nationality =
      "Nationality required hai.";
  }

  if (
    requireAddress &&
    !normalized.address
  ) {
    errors.address =
      "Address required hai.";
  }

  if (
    requireId &&
    !normalized.idType
  ) {
    errors.idType =
      "ID type required hai.";
  }

  if (
    requireId &&
    !normalized.idNumber
  ) {
    errors.idNumber =
      "ID number required hai.";
  }

  const idFrontFile =
    guest.idFrontFile ??
    guest.newIdFrontFile ??
    null;

  const idBackFile =
    guest.idBackFile ??
    guest.newIdBackFile ??
    null;

  if (
    requireIdFront &&
    !idFrontFile &&
    !normalized.idFrontUrl
  ) {
    errors.idFrontFile =
      "ID front photo required hai.";
  }

  if (idFrontFile) {
    const validation =
      validateGuestImageFile(
        idFrontFile
      );

    if (!validation.valid) {
      errors.idFrontFile =
        validation.error ||
        validation.message;
    }
  }

  if (idBackFile) {
    const validation =
      validateGuestImageFile(
        idBackFile
      );

    if (!validation.valid) {
      errors.idBackFile =
        validation.error ||
        validation.message;
    }
  }

  return {
    valid:
      Object.keys(
        errors
      ).length === 0,

    errors,

    normalizedGuest:
      normalized,
  };
}

/* ==========================================================================
   DUPLICATE GUEST CHECK
========================================================================== */

export function findDuplicateGuestsInList(
  guests = []
) {
  const duplicateIndexes =
    new Set();

  guests.forEach(
    (
      firstGuest,
      firstIndex
    ) => {
      const normalizedFirst =
        normalizeGuestProfile(
          firstGuest
        );

      guests.forEach(
        (
          secondGuest,
          secondIndex
        ) => {
          if (
            firstIndex >=
            secondIndex
          ) {
            return;
          }

          const normalizedSecond =
            normalizeGuestProfile(
              secondGuest
            );

          /*
           * Same phone number multiple guests me allowed hai.
           * Couple/family check-in me contact number common ho sakta hai.
           * Duplicate sirf same government ID number ko maana jayega.
           */
          const sameId =
            normalizedFirst.idNumber &&
            normalizedSecond.idNumber &&
            normalizedFirst.idNumber ===
              normalizedSecond.idNumber;

          if (sameId) {
            duplicateIndexes.add(
              firstIndex
            );

            duplicateIndexes.add(
              secondIndex
            );
          }
        }
      );
    }
  );

  return [
    ...duplicateIndexes,
  ].sort(
    (a, b) => a - b
  );
}

/* ==========================================================================
   STORAGE UPLOAD
========================================================================== */

export async function uploadGuestFile({
  hotelId,
  profileId,
  file,
  side = "document",
}) {
  const normalizedHotelId =
    cleanGuestValue(
      hotelId
    );

  const normalizedProfileId =
    cleanGuestValue(
      profileId
    );

  if (!normalizedHotelId) {
    throw new Error(
      "Guest document upload ke liye hotel ID missing hai."
    );
  }

  if (!normalizedProfileId) {
    throw new Error(
      "Guest document upload ke liye profile ID missing hai."
    );
  }

  if (!file) {
    return "";
  }

  const validation =
    validateGuestImageFile(
      file
    );

  if (!validation.valid) {
    throw new Error(
      validation.error ||
        validation.message
    );
  }

  const safeHotelId =
    normalizeFileName(
      normalizedHotelId
    );

  const safeProfileId =
    normalizeFileName(
      normalizedProfileId
    );

  const safeSide =
    normalizeFileName(
      side || "document"
    );

  const extension =
    getFileExtension(
      file
    );

  const randomPart =
    Math.random()
      .toString(36)
      .slice(2, 10);

  const fileName =
    `${safeSide}_` +
    `${Date.now()}_` +
    `${randomPart}.` +
    `${extension}`;

  /*
   * Firebase Storage Rules ke saath exact matching path:
   *
   * guestDocuments/{hotelId}/{guestId}/{fileName}
   */

  const storagePath =
    `guestDocuments/` +
    `${safeHotelId}/` +
    `${safeProfileId}/` +
    `${fileName}`;

  console.log(
    "Guest document upload started:",
    storagePath
  );

  const storageReference =
    ref(
      storage,
      storagePath
    );

  await uploadBytes(
    storageReference,
    file,
    {
      contentType:
        file.type ||
        "image/jpeg",

      customMetadata: {
        hotelId:
          normalizedHotelId,

        guestId:
          normalizedProfileId,

        profileId:
          normalizedProfileId,

        side:
          cleanGuestValue(
            side
          ),
      },
    }
  );

  const downloadUrl =
    await getDownloadURL(
      storageReference
    );

  console.log(
    "Guest document uploaded:",
    storagePath
  );

  return downloadUrl;
}

/* ==========================================================================
   DELETE STORAGE FILE
========================================================================== */

export async function deleteGuestFileByUrl(
  fileUrl
) {
  const normalizedUrl =
    cleanGuestValue(
      fileUrl
    );

  if (!normalizedUrl) {
    return false;
  }

  try {
    const storageReference =
      ref(
        storage,
        normalizedUrl
      );

    await deleteObject(
      storageReference
    );

    return true;
  } catch (error) {
    if (
      error?.code ===
        "storage/object-not-found" ||
      error?.code ===
        "storage/invalid-url"
    ) {
      return false;
    }

    console.error(
      "Guest document delete error:",
      error
    );

    throw error;
  }
}

/* ==========================================================================
   SAVE SINGLE GUEST PROFILE
========================================================================== */

async function saveSingleGuestProfile({
  hotelId,
  hotelName,
  guest,
  index,
  incrementVisitCount = false,
}) {
  const normalizedGuest =
    normalizeGuestProfile(
      guest
    );

  let profileId = normalizedGuest.profileId;
  if (!profileId && normalizedGuest.idNumber) {
    const matches = await getDocs(query(
      collection(db, GUEST_PROFILE_COLLECTION),
      where("hotelId", "==", cleanGuestValue(hotelId))
    ));
    const sameType = matches.docs.filter(item =>
      lowerGuestValue(item.data().idType) === lowerGuestValue(normalizedGuest.idType)
      && normalizeGuestIdNumber(item.data().idNumber) === normalizedGuest.idNumber);
    if (sameType.length > 1) throw new Error("Is ID ke multiple profiles hain. Existing guest profile select karein.");
    profileId = sameType[0]?.id || await guestIdentityKey(hotelId, normalizedGuest.idType, normalizedGuest.idNumber);
  }
  const profileReference = profileId
    ? doc(db, GUEST_PROFILE_COLLECTION, profileId)
    : doc(collection(db, GUEST_PROFILE_COLLECTION));

  const existingSnapshot =
    await getDoc(
      profileReference
    );

  const existingProfile =
    existingSnapshot.exists()
      ? normalizeGuestProfile(
          {
            id:
              existingSnapshot.id,

            profileId:
              existingSnapshot.id,

            ...existingSnapshot.data(),
          }
        )
      : null;

  if (
    existingProfile?.hotelId &&
    existingProfile.hotelId !==
      cleanGuestValue(
        hotelId
      )
  ) {
    throw new Error(
      `${normalizedGuest.name || `Guest ${index + 1}`} ka profile kisi doosre hotel se linked hai.`
    );
  }

  let idFrontUrl =
    cleanGuestValue(
      guest.idFrontUrl
    ) ||
    existingProfile?.idFrontUrl ||
    "";

  let idBackUrl =
    cleanGuestValue(
      guest.idBackUrl
    ) ||
    existingProfile?.idBackUrl ||
    "";

  if (guest.idFrontFile) {
    idFrontUrl =
      await uploadGuestFile({
        hotelId,

        profileId:
          profileReference.id,

        file:
          guest.idFrontFile,

        side:
          "id_front",
      });
  }

  if (guest.idBackFile) {
    idBackUrl =
      await uploadGuestFile({
        hotelId,

        profileId:
          profileReference.id,

        file:
          guest.idBackFile,

        side:
          "id_back",
      });
  }

  const relation =
    index === 0 && guest.isPrimary !== false
      ? "Primary"
      : (normalizedGuest.relation || "Additional Guest");

  const payload = {
    hotelId:
      cleanGuestValue(
        hotelId
      ),

    hotelName:
      cleanGuestValue(
        hotelName
      ),

    name:
      normalizedGuest.name,

    nameLower:
      lowerGuestValue(
        normalizedGuest.name
      ),

    phoneNumber:
      normalizedGuest.phoneNumber,

    phone:
      normalizedGuest.phoneNumber,

    age:
      normalizedGuest.age,

    gender:
      normalizedGuest.gender,

    nationality:
      normalizedGuest.nationality ||
      "Indian",

    address:
      normalizedGuest.address,

    relation,

    idType:
      normalizedGuest.idType,

    idNumber:
      normalizedGuest.idNumber,

    idNumberLower:
      lowerGuestValue(
        normalizedGuest.idNumber
      ),

    idFrontUrl,
    idBackUrl,

    searchText:
      buildGuestSearchText({
        ...normalizedGuest,
        hotelName,
        relation,
      }),

    isVip:
      guest.isVip === true ||
      existingProfile?.isVip ===
        true,

    isBlacklisted:
      guest.isBlacklisted ===
        true ||
      existingProfile
        ?.isBlacklisted === true,

    blacklistReason:
      cleanGuestValue(
        guest.blacklistReason
      ) ||
      existingProfile
        ?.blacklistReason ||
      "",

    notes:
      cleanGuestValue(
        guest.profileNotes ??
          guest.notes
      ) ||
      existingProfile?.notes ||
      "",

    updatedAt:
      serverTimestamp(),

    ...(incrementVisitCount ? {lastVisitAt: serverTimestamp(), lastVisitedAt: serverTimestamp()} : {}),
  };

  const nextVisitCount = await runTransaction(db, async transaction => {
    const latest = await transaction.get(profileReference);
    const current = latest.exists() ? latest.data() : null;
    if (current?.hotelId && current.hotelId !== cleanGuestValue(hotelId)) {
      throw new Error("Guest profile kisi doosre hotel se linked hai.");
    }
    const nextPayload = provisionGuestProfile(current, payload, incrementVisitCount, serverTimestamp());
    transaction.set(profileReference, nextPayload, { merge: true });
    return nextPayload.visitCount;
  });

  return {
    ...existingProfile,
    ...normalizedGuest,

    id:
      profileReference.id,

    profileId:
      profileReference.id,

    hotelId:
      cleanGuestValue(
        hotelId
      ),

    hotelName:
      cleanGuestValue(
        hotelName
      ),

    relation,

    idFrontUrl,
    idBackUrl,

    visitCount:
      nextVisitCount,

    isPrimary:
      guest.isPrimary === false ? false : (index === 0 || guest.isPrimary === true),
  };
}

/* ==========================================================================
   SAVE ALL GUEST PROFILES
========================================================================== */

export async function saveGuestProfiles({
  hotelId,
  hotelName = "",
  guests = [],
  validate = true,
  incrementVisitCount = false,
}) {
  const normalizedHotelId =
    cleanGuestValue(
      hotelId
    );

  if (!normalizedHotelId) {
    throw new Error(
      "Guest profiles save karne ke liye hotel ID missing hai."
    );
  }

  if (
    !Array.isArray(guests) ||
    guests.length === 0
  ) {
    throw new Error(
      "Kam se kam ek guest required hai."
    );
  }

  const duplicateIndexes =
    findDuplicateGuestsInList(
      guests
    );

  if (
    duplicateIndexes.length > 0
  ) {
    const guestNumbers =
      duplicateIndexes
        .map(
          (index) =>
            index + 1
        )
        .join(", ");

    throw new Error(
      `Same ID number multiple guests me use hua hai. Guest number: ${guestNumbers}`
    );
  }

  if (validate) {
    guests.forEach(
      (
        guest,
        index
      ) => {
        const validation =
          validateGuestProfile(
            guest,
            {
              requirePhone:
                index === 0,

              requireId: true,

              requireAddress:
                true,

              requireAge: true,

              requireIdFront:
                true,
            }
          );

        if (
          !validation.valid
        ) {
          const firstError =
            Object.values(
              validation.errors
            )[0];

          throw new Error(
            `Guest ${index + 1}: ${
              firstError ||
              "Details incomplete hain."
            }`
          );
        }
      }
    );
  }

  const savedGuests = [];

  /*
   * Guests sequentially save honge.
   * Weak internet par multiple ID uploads
   * ek saath start nahi honge.
   */

  for (
    let index = 0;
    index < guests.length;
    index += 1
  ) {
    const savedGuest =
      await saveSingleGuestProfile(
        {
          hotelId:
            normalizedHotelId,

          hotelName,

          guest:
            guests[index],

          index,

          incrementVisitCount,
        }
      );

    savedGuests.push(
      savedGuest
    );
  }

  return savedGuests;
}

/* ==========================================================================
   GET SINGLE PROFILE
========================================================================== */

export async function getGuestProfileById(
  profileId
) {
  const normalizedProfileId =
    cleanGuestValue(
      profileId
    );

  if (!normalizedProfileId) {
    return null;
  }

  const profileSnapshot =
    await getDoc(
      doc(
        db,
        GUEST_PROFILE_COLLECTION,
        normalizedProfileId
      )
    );

  if (
    !profileSnapshot.exists()
  ) {
    return null;
  }

  return normalizeGuestProfile(
    {
      id:
        profileSnapshot.id,

      profileId:
        profileSnapshot.id,

      ...profileSnapshot.data(),
    }
  );
}

/* ==========================================================================
   UPDATE VIP STATUS
========================================================================== */

export async function updateGuestVipStatus({
  profileId,
  isVip,
}) {
  const normalizedProfileId =
    cleanGuestValue(
      profileId
    );

  if (!normalizedProfileId) {
    throw new Error(
      "VIP status update ke liye profile ID missing hai."
    );
  }

  await setDoc(
    doc(
      db,
      GUEST_PROFILE_COLLECTION,
      normalizedProfileId
    ),
    {
      isVip:
        Boolean(isVip),

      guestTag:
        isVip
          ? "vip"
          : "normal",

      updatedAt:
        serverTimestamp(),
    },
    {
      merge: true,
    }
  );

  return true;
}

/* ==========================================================================
   UPDATE BLACKLIST STATUS
========================================================================== */

export async function updateGuestBlacklistStatus({
  profileId,
  isBlacklisted,
  blacklistReason = "",
}) {
  const normalizedProfileId =
    cleanGuestValue(
      profileId
    );

  if (!normalizedProfileId) {
    throw new Error(
      "Blacklist update ke liye profile ID missing hai."
    );
  }

  await setDoc(
    doc(
      db,
      GUEST_PROFILE_COLLECTION,
      normalizedProfileId
    ),
    {
      isBlacklisted:
        Boolean(
          isBlacklisted
        ),

      guestTag:
        isBlacklisted
          ? "blacklist"
          : "normal",

      blacklistReason:
        isBlacklisted
          ? cleanGuestValue(
              blacklistReason
            )
          : "",

      updatedAt:
        serverTimestamp(),
    },
    {
      merge: true,
    }
  );

  return true;
}

/* ==========================================================================
   DEFAULT EXPORT
========================================================================== */

const guestProfileService = {
  buildGuestSearchText,
  cleanGuestValue,
  deleteGuestFileByUrl,
  filterGuestProfiles,
  findDuplicateGuestsInList,
  getGuestProfileById,
  loadGuestProfiles,
  lowerGuestValue,
  normalizeGuestIdNumber,
  normalizeGuestPhone,
  normalizeGuestProfile,
  saveGuestProfiles,
  updateGuestBlacklistStatus,
  updateGuestVipStatus,
  uploadGuestFile,
  validateGuestImageFile,
  validateGuestProfile,
};
export default guestProfileService;
